import React, { useEffect, useRef, useState } from 'react';
import { ZoneInfo, ZoneWithPolygon } from 'types/Delivery';
import rfdc from 'rfdc';

const clone = rfdc({ proto: false });

function getPixelDistance(
  map: google.maps.Map,
  a: google.maps.LatLng,
  b: google.maps.LatLng
): number {
  const proj = map.getProjection();
  if (!proj) return Infinity;
  const scale = Math.pow(2, map.getZoom() ?? 0);
  const pa = proj.fromLatLngToPoint(a)!;
  const pb = proj.fromLatLngToPoint(b)!;
  const dx = (pa.x - pb.x) * scale;
  const dy = (pa.y - pb.y) * scale;
  return Math.sqrt(dx * dx + dy * dy);
}

function useDrawingZone(
  initZone: Array<ZoneInfo>,
  editingZoneIndex: number,
  setPolluted: React.Dispatch<React.SetStateAction<boolean>>
) {
  const polygons = useRef<Array<ZoneWithPolygon>>(clone(initZone));

  const [map, setMap] = useState<any>(null);
  const [isInitPolygon, setIsInitPolygon] = useState(false);
  const [isActiveDraw, setActiveDraw] = useState(false);
  const [editedZone, setEditedZone] = useState(initZone);

  const editedZoneRef = useRef(editedZone);

  useEffect(() => {
    editedZoneRef.current = editedZone;
  }, [editedZone]);

  useEffect(() => {
    setEditedZone(initZone);
  }, [initZone]);

  useEffect(() => {
    if (!map) {
      return;
    }
    if (isInitPolygon) {
      return;
    }
    let initPoly = false;

    initZone.forEach((item, index) => {
      if (!item.coords.length) {
        return;
      }
      const polygon = new google.maps.Polygon({
        paths: item.coords,
        editable: true,
        strokeColor: item.color,
        fillColor: item.color,
        fillOpacity: 0.3
      });
      if (item.enabled) {
        polygon.setMap(map);
      }
      polygon.setEditable(false);
      polygon.setDraggable(false);

      polygons.current[index].polygon = polygon;
      if (!initPoly) {
        initPoly = true;
      }
    });
    setIsInitPolygon(initPoly);
  }, [initZone, map, isInitPolygon]);

  useEffect(() => {
    polygons.current.forEach((item, index) => {
      if (!item.polygon) return;
      const isEditing = index === editingZoneIndex;
      item.polygon.setEditable(isEditing);
      item.polygon.setDraggable(false);
    });
  }, [editingZoneIndex]);

  useEffect(() => {
    if (!map || !isActiveDraw || editingZoneIndex === -1) {
      return;
    }

    let firstMarker: google.maps.Marker | null = null;
    let previewPolyline: google.maps.Polyline | null = null;

    const color = initZone[editingZoneIndex].color;
    const vertices: google.maps.LatLng[] = [];
    const vertexMarkers: google.maps.Marker[] = [];

    const polyline = new google.maps.Polyline({
      map,
      strokeColor: color,
      strokeWeight: 2,
      strokeOpacity: 0.8,
      clickable: false
    });

    const polygonOptions: google.maps.PolygonOptions = {
      editable: true,
      strokeColor: color,
      fillColor: color,
      fillOpacity: 0.3
    };

    function completePolygon() {
      if (vertices.length < 3) return;

      polyline.setMap(null);
      previewPolyline?.setMap(null);
      previewPolyline = null;
      vertexMarkers.forEach((m) => m.setMap(null));
      vertexMarkers.length = 0;

      const polygon = new google.maps.Polygon({
        paths: [...vertices],
        ...polygonOptions
      });
      polygon.setMap(map);

      const pathArr = polygon
        .getPath()
        .getArray()
        .map((item) => item.toJSON());
      const newEditedZone = clone(editedZoneRef.current);
      const newEditZoneItem = newEditedZone[editingZoneIndex];
      newEditZoneItem.coords = pathArr;
      newEditZoneItem.isPolygon = true;
      polygons.current[editingZoneIndex].polygon = polygon;
      setEditedZone(newEditedZone);
      setActiveDraw(false);
      setPolluted(true);
      setIsInitPolygon(true);
    }

    const mapMoveListener = google.maps.event.addListener(
      map,
      'mousemove',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng || vertices.length === 0) return;
        if (!previewPolyline) {
          previewPolyline = new google.maps.Polyline({
            map,
            strokeColor: color,
            strokeWeight: 2,
            strokeOpacity: 0.4,
            clickable: false
          });
        }
        previewPolyline.setPath([vertices[vertices.length - 1], e.latLng]);
      }
    );

    const mapClickListener = google.maps.event.addListener(
      map,
      'click',
      (e: google.maps.MapMouseEvent) => {
        if (!e.latLng) return;

        if (vertices.length >= 3) {
          if (getPixelDistance(map, e.latLng, vertices[0]) < 15) {
            completePolygon();
            return;
          }
        }

        vertices.push(e.latLng);
        polyline.getPath().push(e.latLng);

        if (vertices.length === 1) {
          firstMarker = new google.maps.Marker({
            position: e.latLng,
            map,
            cursor: 'pointer',
            title: 'Click to close polygon',
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 8,
              fillColor: color,
              fillOpacity: 1,
              strokeWeight: 2,
              strokeColor: '#ffffff'
            }
          });
          google.maps.event.addListener(firstMarker, 'click', () => {
            if (vertices.length >= 3) {
              completePolygon();
            }
          });
          vertexMarkers.push(firstMarker);
        } else {
          const dot = new google.maps.Marker({
            position: e.latLng,
            map,
            clickable: false,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 5,
              fillColor: color,
              fillOpacity: 1,
              strokeWeight: 1.5,
              strokeColor: '#ffffff'
            }
          });
          vertexMarkers.push(dot);
        }
      }
    );

    return () => {
      mapClickListener.remove();
      mapMoveListener.remove();
      polyline.setMap(null);
      previewPolyline?.setMap(null);
      vertexMarkers.forEach((m) => m.setMap(null));
      vertexMarkers.length = 0;
    };
  }, [isActiveDraw, map, editingZoneIndex, initZone, setPolluted]);

  const deleteZone = (index: number) => {
    const newEditedZone = clone(editedZone);
    const newEditZoneItem = newEditedZone[index];
    newEditZoneItem.coords = [];
    newEditZoneItem.isPolygon = false;
    setActiveDraw(false);
    setEditedZone(newEditedZone);
    if (typeof polygons.current[index].polygon !== 'undefined') {
      polygons.current[index].polygon?.setMap(null);
      polygons.current[index].polygon = undefined;
    }
    setPolluted(false);
  };

  return {
    zone: editedZone,
    zoneWithPolygon: polygons.current,
    isActiveDraw: isActiveDraw,
    deleteZone: deleteZone,
    setMapInstance: setMap,
    setActiveDraw: setActiveDraw
  };
}

export default useDrawingZone;
